import { useContext, useEffect } from "react";
import {
  Flex,
  PaginationContext,
  Text,
  PageViewContext,
  PageViewTypeEnum,
  PageViewToggle,
  Box,
  SearchContext,
  Select,
  useScreenResolution,
  FilterContext,
} from "dash-ui";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import PropertiesListView from "./components/PropertiesListView/PropertiesListView";
import PropertiesMapView from "./components/PropertiesMapView/PropertiesMapView";
import {
  PROPERTY_VIEW_SORT_VALUES,
  PropertyContentViewSortOptionsEnum,
} from "./PropertiesContentView.constants";

const PropertiesContentView = () => {
  const theme = useTheme();
  const {
    devices: { isMobile },
  } = useScreenResolution();
  const { state: filterState, setFilterState } = useContext(FilterContext);
  const { t } = useTranslation(["common", "pages"]);
  const { state } = useContext(PaginationContext);
  const { viewType } = useContext(PageViewContext);
  const { searchSlug } = useContext(SearchContext);

  function setSortOptions(value: any) {
    // Default sorting state
    if (!value) {
      setFilterState("sort", {
        sortOptions: { fieldName: "updatedAt", sortOrder: -1 },
      });
      return;
    }

    setFilterState("sort", {
      sortOptions:
        PROPERTY_VIEW_SORT_VALUES[value as PropertyContentViewSortOptionsEnum],
      selectedValue: value,
    });
  }

  // Effect to set default sorting state. Dealing with the dash-ui's `Select` component is difficult so this is
  // our only option for now.
  useEffect(() => {
    if (viewType === PageViewTypeEnum.MAP) return;

    setFilterState("sort", {
      sortOptions: { fieldName: "updatedAt", sortOrder: -1 },
    });
  }, []);

  const getPropertiesResultText = () => {
    return (
      <>
        {viewType === PageViewTypeEnum.LIST ? (
          <>
            {t("pages:propertyPage.resultsFor", {
              total: state.total,
              location: searchSlug
                ? `${t("common:labels.for")} ${searchSlug}`
                : "",
            })}
          </>
        ) : (
          ""
        )}
      </>
    );
  };

  return (
    <Flex flexDirection="column" w="100%" flex={1}>
      <div
        className={`flex flex-col w-full md:flex-row md:justify-between md:items-center my-4`}
      >
        <div className="mt-4 flex flex-row gap-2 items-center md:mt-0 order-2 md:order-none">
          <PageViewToggle
            buttons={[
              { icon: "listStraight", viewType: PageViewTypeEnum.LIST },
              {
                icon: "pin",
                viewType: PageViewTypeEnum.MAP,
              },
            ]}
          />
          <Box ml={4}>
            <Text color={theme.colors.n70}>{getPropertiesResultText()}</Text>
          </Box>
        </div>
        {viewType === PageViewTypeEnum.LIST && (
          <Select
            menuPlacementHorizontal={isMobile ? "left" : "right"}
            menuPlacement="bottom"
            isSearchable={false}
            leftIconColor={theme.colors.n80}
            leftIcon={"compareArrows"}
            value={filterState?.sort?.sortOptions?.fieldName || "updatedAt"}
            options={Object.values(PropertyContentViewSortOptionsEnum).map(
              (item) => ({
                label: t(`common:sortOptions.${item}`),
                value: item,
              })
            )}
            onChanged={(value) => setSortOptions(value)}
            placeholder={t("pages:propertyPage.sortBy")}
            className="w-full md:w-fit md:mt-0"
          />
        )}
      </div>
      {viewType === PageViewTypeEnum.MAP ? (
        <PropertiesMapView />
      ) : (
        <PropertiesListView />
      )}
    </Flex>
  );
};

export default PropertiesContentView;
